import { useState, useEffect } from "react";
import { mailingApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { EmailBlockEditor } from "./EmailBlockEditor";
import { TemplateCard } from "./TemplateCard";
import { HeroImageField } from "./HeroImageField";
import { SpellcheckDialog } from "./SpellcheckDialog";
import { EmailPreviewDialog } from "./EmailPreviewDialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { createBlock, textToBlocks, blocksToPlainText } from "./emailBlocks";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, FileText, ArrowLeft } from "lucide-react";

export const TemplateManagerDialog = ({ open, onOpenChange, templates, onChanged }) => {
  const toast = useToast();
  const [mode, setMode] = useState("list"); // list | edit
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [heroImage, setHeroImage] = useState("");
  const [preheader, setPreheader] = useState("");
  const [status, setStatus] = useState("Entwurf");
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [spellcheckOpen, setSpellcheckOpen] = useState(false);
  const [review, setReview] = useState(null); // { diffs, subject, blocks, suggested_subject, suggested_preview }
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [previewData, setPreviewData] = useState(null); // { html, blocks, subject }
  const [stats, setStats] = useState({});

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await mailingApi.templateStats();
        setStats(res.data || {});
      } catch {
        setStats({});
      }
    })();
  }, [open, templates]);

  const resetForm = () => {
    setEditId(null);
    setName("");
    setSubject("");
    setBlocks([createBlock("text")]);
    setHeroImage("");
    setPreheader("");
    setStatus("Entwurf");
  };

  const openCreate = () => {
    resetForm();
    setMode("edit");
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setName(t.name || "");
    setSubject(t.subject || "");
    setHeroImage(t.hero_image || "");
    setPreheader(t.preheader || "");
    setStatus(t.status || "Entwurf");
    if (t.content_type === "blocks" && Array.isArray(t.blocks) && t.blocks.length) {
      setBlocks(t.blocks.map((b) => ({ ...b })));
    } else {
      setBlocks(textToBlocks(t.body || ""));
    }
    setMode("edit");
  };

  const backToList = () => {
    resetForm();
    setMode("list");
  };

  // Persistiert die Vorlage (mit ggf. korrigiertem Betreff/Blöcken/Vorschauzeile).
  const doSave = async (subjectToSave, blocksToSave, preheaderToSave) => {
    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        subject: (subjectToSave ?? subject).trim(),
        content_type: "blocks",
        blocks: blocksToSave ?? blocks,
        body: blocksToPlainText(blocksToSave ?? blocks),
        hero_image: heroImage || null,
        preheader: (preheaderToSave ?? preheader) || null,
        status: status || "Entwurf",
      };
      if (editId) {
        await mailingApi.updateTemplate(editId, data);
        toast.success("Vorlage aktualisiert");
      } else {
        await mailingApi.createTemplate(data);
        toast.success("Vorlage erstellt");
      }
      await onChanged();
      setSpellcheckOpen(false);
      setReview(null);
      backToList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Bitte einen Namen angeben");
      return;
    }
    // Automatische KI-Prüfung vor dem Speichern (Rechtschreibung + Betreff-/Vorschauzeilen-Vorschlag)
    setSaving(true);
    try {
      const res = await mailingApi.spellcheckBlocks({ subject, blocks });
      const r = res.data || {};
      const hasSubjectSug = r.suggested_subject && r.suggested_subject.trim() !== (subject || "").trim();
      const needsReview = (r.changed && (r.diffs || []).length) || hasSubjectSug || r.suggested_preview;
      if (needsReview) {
        setReview(r);
        setSpellcheckOpen(true);
        setSaving(false);
        return;
      }
    } catch (error) {
      // KI nicht verfügbar o. Ä. → normal weiterspeichern (Prüfung ist optional)
      console.debug("KI-Prüfung übersprungen", error);
    }
    setSaving(false);
    await doSave();
  };

  // Aus dem Review-Dialog: Vorschläge (je nach Auswahl) übernehmen und speichern.
  const handleAcceptReview = (useSubject, usePreview) => {
    const subjectToSave = useSubject && review?.suggested_subject ? review.suggested_subject : (review?.subject ?? subject);
    const preheaderToSave = usePreview && review?.suggested_preview ? review.suggested_preview : preheader;
    doSave(subjectToSave, review?.blocks ?? blocks, preheaderToSave);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await mailingApi.deleteTemplate(toDelete.id);
      toast.success("Vorlage gelöscht");
      await onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    } finally {
      setToDelete(null);
    }
  };

  const handleDuplicate = async (t) => {
    setDuplicatingId(t.id);
    try {
      await mailingApi.duplicateTemplate(t.id);
      toast.success("Vorlage dupliziert");
      await onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Duplizieren");
    } finally {
      setDuplicatingId(null);
    }
  };

  const handlePreview = async (t, cachedHtml) => {
    if (cachedHtml) {
      setPreviewData({ html: cachedHtml, blocks: t.blocks || [], subject: t.subject || t.name });
      return;
    }
    try {
      const res = await mailingApi.previewHtml({
        subject: t.subject || t.name,
        content_type: t.content_type || "text",
        blocks: t.blocks,
        message: t.body || "",
        hero_image: t.hero_image || null,
        preheader: t.preheader || null,
      });
      setPreviewData({ html: res.data.html || "", blocks: t.blocks || [], subject: t.subject || t.name });
    } catch {
      toast.error("Vorschau konnte nicht geladen werden");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) backToList(); onOpenChange(o); }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="template-manager-dialog">
          {mode === "list" ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> E-Mail-Vorlagen</DialogTitle>
                <DialogDescription>Vorlagen anlegen, bearbeiten oder löschen. Sie erscheinen im Rundmail-Dialog.</DialogDescription>
              </DialogHeader>
              <div className="flex justify-end">
                <Button size="sm" onClick={openCreate} data-testid="template-add-btn">
                  <Plus className="w-4 h-4 mr-2" /> Neue Vorlage
                </Button>
              </div>
              <ScrollArea className="max-h-[55vh] pr-3">
                {templates.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">Noch keine Vorlagen</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {templates.map((t) => (
                      <TemplateCard
                        key={t.id}
                        template={t}
                        stats={stats[t.id]}
                        onEdit={openEdit}
                        onDelete={setToDelete}
                        onDuplicate={handleDuplicate}
                        onPreview={handlePreview}
                        duplicating={duplicatingId === t.id}
                      />
                    ))}
                  </div>
                )}
              </ScrollArea>
              <DialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)}>Schließen</Button>
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={handleSave}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <button type="button" onClick={backToList} className="text-muted-foreground hover:text-foreground" data-testid="template-back-btn">
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                  {editId ? "Vorlage bearbeiten" : "Neue Vorlage"}
                </DialogTitle>
                <DialogDescription>Platzhalter wie [Datum] können im Text verwendet werden.</DialogDescription>
              </DialogHeader>
              <div className="py-2 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="tpl-name">Name *</Label>
                  <Input id="tpl-name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="template-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tpl-subject">Betreff</Label>
                  <Input id="tpl-subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="template-subject-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tpl-status">Status</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger id="tpl-status" className="w-full" data-testid="template-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Entwurf" data-testid="template-status-entwurf">Entwurf</SelectItem>
                      <SelectItem value="Final" data-testid="template-status-final">Final</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tpl-preheader">Vorschauzeile (Postfach-Vorschau)</Label>
                  <Input id="tpl-preheader" value={preheader} onChange={(e) => setPreheader(e.target.value)} placeholder="Kurzer Vorschautext – erscheint im Postfach neben dem Betreff" data-testid="template-preheader-input" />
                </div>
                <HeroImageField value={heroImage} onChange={setHeroImage} />
                <div className="space-y-2">
                  <Label>Inhalt</Label>
                  <ScrollArea className="max-h-[45vh] pr-3">
                    <EmailBlockEditor blocks={blocks} onChange={setBlocks} />
                  </ScrollArea>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={backToList}>Zurück</Button>
                <Button type="submit" disabled={saving} data-testid="template-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Speichern
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <SpellcheckDialog
        open={spellcheckOpen}
        onOpenChange={setSpellcheckOpen}
        diffs={review?.diffs || []}
        currentSubject={subject}
        suggestedSubject={review?.suggested_subject || ""}
        suggestedPreview={review?.suggested_preview || ""}
        saving={saving}
        onSave={handleAcceptReview}
        onSkip={() => doSave()}
      />

      <EmailPreviewDialog
        open={!!previewData}
        onOpenChange={(o) => { if (!o) setPreviewData(null); }}
        html={previewData?.html || ""}
        blocks={previewData?.blocks || []}
        subject={previewData?.subject || ""}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Vorlage löschen?</AlertDialogTitle>
            <AlertDialogDescription>„{toDelete?.name}" wird dauerhaft entfernt.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-template-btn">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
